import React from "react";
import { useGlossaryDataContext } from "@/providers/ObjectsDataProvider";
import { MultiSelectDropdownWithField, MultiSelectOption } from "@/components/ui/dropdowns";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/user-feedback/tooltip";
import { getCachedLibraryItems } from "@/lib/stores/objectsStore";
import { useSimplifiedUserData } from "@/providers/SimplifiedUserDataProvider";

interface GlossarySelectionElementProps {
  value: string | string[];
  onChange: (value: string | string[]) => void;
  onBlur?: (value: string | string[]) => void;
  placeholder?: string;
  multiple?: boolean;
  filterOptions?: string[]; // Array of blueprint/template IDs to filter by
  disabled?: boolean;
}

export const GlossarySelectionElement: React.FC<GlossarySelectionElementProps> = ({
  value,
  onChange,
  onBlur,
  placeholder = "Select objects...",
  multiple = false,
  filterOptions = [],
  disabled = false,
}) => {
  // Convert string value to array for multiple mode, keep as-is for single mode
  const dropdownValue = multiple && typeof value === 'string' 
    ? (value ? value.split(',') : [])
    : value;

  // Handle onChange conversion: array -> string for multiple, keep as-is for single
  const handleDropdownChange = (newValue: string | string[]) => {
    if (multiple && Array.isArray(newValue)) {
      // Convert array to comma-separated string
      onChange(newValue.join(','));
    } else {
      // Single mode or already a string
      onChange(newValue);
    }
  };

  // Handle onBlur conversion
  const handleDropdownBlur = (newValue: string | string[]) => {
    if (onBlur) {
      if (multiple && Array.isArray(newValue)) {
        // Convert array to comma-separated string
        onBlur(newValue.join(','));
      } else {
        // Single mode or already a string
        onBlur(newValue);
      }
    }
  };

  const { isLoading } = useGlossaryDataContext();
  const { selectedGame } = useSimplifiedUserData();
  
  // Get all library items (both glossary and document types) for selection
  const allGlossaryItems = selectedGame?.id ? getCachedLibraryItems(selectedGame.id) : [];
  
  // Filter glossary items based on filterOptions (blueprint/template IDs) if filter is enabled
  const availableGlossaryItems = filterOptions.length > 0 
    ? allGlossaryItems.filter(item => 
        // Check if the item was created from one of the specified templates/blueprints
        item.blueprint_id && filterOptions.includes(item.blueprint_id)
      )
    : allGlossaryItems;

  // Check if we should disable the dropdown
  const hasNoAvailableItems = availableGlossaryItems.length === 0;
  
  // Only disable if there are no available items, not when there are no filter options
  // When there are no filter options, we should show all available items
  const shouldDisable = hasNoAvailableItems;
  const finalDisabled = disabled || shouldDisable;

  // Convert glossary items to MultiSelectOption format
  const glossaryOptions: MultiSelectOption[] = availableGlossaryItems.map(item => ({
    id: item.id,
    label: item.name,
    description: `Type: ${item.type || 'N/A'} | Tags: ${item.tags?.join(', ') || 'None'}`,
    parentTypeId: item.type || 'unknown',
    parentTypeName: item.type || 'Unknown Type'
  }));

  // Create the dropdown component
  const dropdownComponent = (
    <MultiSelectDropdownWithField
      value={dropdownValue}
      onChange={handleDropdownChange}
      onBlur={handleDropdownBlur}
      options={glossaryOptions}
      placeholder={placeholder}
      multiple={multiple}
      disabled={finalDisabled}
      isLoading={isLoading}
    />
  );

  // If we should disable due to no options, wrap with tooltip
  if (shouldDisable) {
    return (
      <TooltipProvider delayDuration={100} skipDelayDuration={0}>
        <Tooltip>
          <TooltipTrigger asChild>
            {dropdownComponent}
          </TooltipTrigger>
          <TooltipContent>
            <p>
              {filterOptions.length > 0 
                ? "No objects available from the specified templates. Please check your filter options or create items with the required templates."
                : "No objects available. Please create some objects first."
              }
            </p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }

  // Otherwise return the dropdown without tooltip
  return dropdownComponent;
};
